import React, { useState } from 'react';
import { Modal, Form, Input, Select, Button, message, Result } from 'antd';
import { MessageOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';

const { TextArea } = Input;

interface TicketButtonProps {
  isMobile: boolean;
  userEmail?: string;
  userName?: string;
}

interface TicketFormData {
  subject: string;
  category: string;
  description: string;
}

const SHEETDB_API_URL = 'https://sheetdb.io/api/v1/j734b8hwc0zh1';

export const TicketButton: React.FC<TicketButtonProps> = ({ isMobile, userEmail, userName }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [form] = Form.useForm();
  const location = useLocation();

  const getCurrentPage = () => {
    const path = location.pathname;
    if (path.includes('dashboard')) return 'Dashboard';
    if (path.includes('expenses')) return 'Expenses';
    if (path.includes('files')) return 'Files';
    return 'Other';
  };

  const handleSubmit = async (values: TicketFormData) => {
    setLoading(true);

    const ticketData = {
      id: `TKT-${Date.now()}`,
      email: userEmail || 'Unknown',
      name: userName || 'Unknown',
      subject: values.subject,
      category: values.category,
      description: values.description,
      page: getCurrentPage(),
      created_at: new Date().toISOString(),
      status: 'New',
    };

    try {
      const response = await fetch(SHEETDB_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: ticketData }),
      });

      if (response.ok) {
        form.resetFields();
        setShowSuccess(true);
      } else {
        throw new Error('Failed to submit ticket');
      }
    } catch (error) {
      console.error('Error submitting ticket:', error);
      message.error('Failed to submit ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        onClick={() => setIsModalOpen(true)}
        style={{
          color: 'white',
          fontSize: 18,
          padding: 8,
          borderRadius: 8,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.2s',
          marginRight: isMobile ? 4 : 8,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
        title="Submit Feedback"
      >
        <MessageOutlined />
      </div>

      <Modal
        title={showSuccess ? null : "Submit Feedback"}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setShowSuccess(false);
        }}
        footer={null}
        width={isMobile ? '95%' : 500}
      >
        {showSuccess ? (
          <Result
            status="success"
            title="Feedback Submitted!"
            subTitle="Thank you for your feedback. We'll review it and get back to you if needed."
            extra={[
              <Button
                type="primary"
                key="close"
                onClick={() => {
                  setIsModalOpen(false);
                  setShowSuccess(false);
                }}
              >
                Close
              </Button>,
            ]}
          />
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              name="subject"
              label="Subject"
              rules={[{ required: true, message: 'Please enter a subject' }]}
            >
              <Input placeholder="Brief description of your issue" />
            </Form.Item>

            <Form.Item
              name="category"
              label="Category"
              rules={[{ required: true, message: 'Please select a category' }]}
            >
              <Select placeholder="Select a category">
                <Select.Option value="Bug">Bug / Issue</Select.Option>
                <Select.Option value="Feature Request">Feature Request</Select.Option>
                <Select.Option value="Question">Question</Select.Option>
                <Select.Option value="Data Issue">Data Issue</Select.Option>
                <Select.Option value="Other">Other</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: 'Please describe your issue' }]}
            >
              <TextArea
                rows={4}
                placeholder="Please provide details about your issue or request..."
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Button onClick={() => setIsModalOpen(false)} style={{ marginRight: 8 }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Submit
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default TicketButton;
